// Properties tab, in three collapsible sections — Attributes (labels, plants,
// layer, dates, notes), Dimensions (measurements and sizes) and Appearance
// (the shared controls in ./appearance). Single selection: the full editor for
// one element. Multiple: mass edit — attributes and appearance apply to the
// whole selection in one undo step, with (mixed) where the selection
// disagrees. No selection while a draw tool is active: the appearance and
// dates the next shape will be drawn with (v1's draw menu).

import { esc, cap, icon } from './html';
import { appearanceHtml, widthToDisplay, widthUnit } from './appearance';
import { elementArea, elementLength, elementPerimeter, segmentLengths, formatLength, formatArea } from '../model/measure';
import { displaySideLength, straightPathSides } from '../path_sides';
import { hasDrawOptions, defaultDrawStyles } from '../model/draw_style';
import { DATE_PLACEHOLDER, DATE_HINT } from '../dates';
import { pathSegments, segmentType, SEGMENT_TYPES } from '../model/segments';
import { speciesValue, speciesKeyOptions } from '../model/labels';

// Open/closed per section. `collapsed` lives on the controller (page session
// only) because the panel is rebuilt on every store change.
const DEFAULT_COLLAPSED = {
  segment: false, attributes: false, dimensions: true, appearance: true,
  'draw-appearance': false, 'draw-attributes': false,
};
export function isSectionCollapsed(collapsed = {}, key) {
  return collapsed[`props-${key}`] ?? DEFAULT_COLLAPSED[key];
}

// Collapsed bodies stay in the DOM (hidden), so their fields keep working for
// anything that reads them and expanding costs no rebuild.
function section(key, title, body, collapsed) {
  if (!body) return '';
  const closed = isSectionCollapsed(collapsed, key);
  return `
    <section class="pl2-props-section" data-section="${key}">
      <button class="pl2-section-title pl2-collapse ${closed ? 'is-collapsed' : ''}" type="button" data-pl2="toggle-section" data-section="${key}" aria-expanded="${!closed}">${title}</button>
      <div class="pl2-props-section__body"${closed ? ' hidden' : ''}>${body}</div>
    </section>`;
}

// `imageSize(el)` -> { width, height } of a loaded image (for images still at
// their natural size), or null. `collapsed`: see isSectionCollapsed.
// `labelEditing`: the label whose pencil is open (see labelsHtml).
export function propsPanelHtml(state, { imageSize = () => null, collapsed = {}, labelEditing = null } = {}) {
  const sel = state.selection;
  if (sel.length === 0) {
    if (hasDrawOptions(state.activeTool)) return drawPropsHtml(state, collapsed);
    return '<h3 class="pl2-section-title">Properties</h3><p class="pl2-muted">Nothing selected.</p>';
  }
  if (sel.length > 1) return massPropsHtml(state, sel, collapsed);
  const el = state.elements.find((e) => e.id === sel[0]);
  if (!el) return '<h3 class="pl2-section-title">Properties</h3>';
  const d = el.data || {};
  const units = state.settings.units;
  const isImage = el.type === 'image';
  return `
    <h3 class="pl2-section-title">${esc(cap(el.type))}</h3>
    ${segmentSection(state, el, units, collapsed)}
    ${section('attributes', 'Attributes', isImage ? imageAttributesHtml(state, d) : attributesHtml(state, el, labelEditing), collapsed)}
    ${section('dimensions', 'Dimensions', isImage ? imageDimensionsHtml(d, imageSize(el), units) : dimensionsHtml(el, units, state.selectedSegment), collapsed)}
    ${section('appearance', 'Appearance', appearanceHtml(d, { type: el.type, units }), collapsed)}`;
}

// --- Attributes --------------------------------------------------------------

function layerField(state, current, mixed = false) {
  const opts = state.layers.map((l) => `<option value="${esc(l.id)}" ${current === l.id ? 'selected' : ''}>${esc(l.name)}</option>`).join('');
  return `<div class="pl2-field"><label>Layer</label><select class="pl2-input" data-pl2="prop-layer"><option value="" ${!current ? 'selected' : ''}>${mixed ? '(mixed)' : '—'}</option>${opts}</select></div>`;
}

// A year, a year and month, or a full date (normalized on change — see
// dates.normalizeDate); the tooltip says so.
function datesFields(added, removed, { mixedAdded = false, mixedRemoved = false } = {}) {
  const field = (label, kind, value, mixed) => `<div class="pl2-field"><label>${label}</label><input class="pl2-input" type="text" placeholder="${mixed ? '(mixed)' : DATE_PLACEHOLDER}" data-pl2="${kind}" data-tip="${esc(DATE_HINT)}" value="${esc(value || '')}"></div>`;
  return field('Added', 'prop-addedDate', added, mixedAdded) + field('Removed', 'prop-removedDate', removed, mixedRemoved);
}

const notesField = (notes) => `<div class="pl2-field"><label>Notes</label><textarea class="pl2-input" rows="2" data-pl2="prop-notes" placeholder="Notes…">${esc(notes || '')}</textarea></div>`;

const subTitle = (text) => `<div class="pl2-subtitle">${text}</div>`;

function plantThumb(sp) {
  return sp?.thumb ? `<img class="pl2-plant-thumb" src="${esc(sp.thumb)}" alt="">` : '<span class="pl2-plant-thumb"></span>';
}

// Labels are inert rows (drag anywhere to reorder); the pencil opens editing —
// the text for a custom label, the shown values for a species label.
// `editing`: { elId, i, order? } of the label being edited, or null — `order`
// pins a species label's option order while it's open (see speciesKeyOptions).
function labelsHtml(state, el, editing) {
  const d = el.data || {};
  const rows = (d.labels || []).map((lb, i) => {
    const open = editing?.elId === el.id && editing.i === i;
    const attrs = `data-el-id="${esc(el.id)}" data-i="${i}"`;
    const pencil = `<button class="pl2-iconbtn pl2-label-edit ${open ? 'is-active' : ''}" type="button" data-pl2="label-edit" ${attrs} aria-pressed="${open}" title="${open ? 'Done' : 'Edit label'}" aria-label="${open ? 'Done editing' : 'Edit label'}">${icon(open ? 'check-line' : 'pencil-line')}</button>`;
    const grip = '<span class="pl2-drag" aria-hidden="true">⠿</span>';
    if (lb.type === 'custom') {
      const text = open
        ? `<input class="pl2-input" data-pl2="label-text" ${attrs} value="${esc(lb.value || '')}" aria-label="Label text">`
        : `<span class="pl2-label-row__text">${lb.value ? esc(lb.value) : '<span class="pl2-muted">(empty)</span>'}</span>`;
      return `<div class="pl2-label-row" data-label-i="${i}">
        ${grip}
        <input type="checkbox" data-pl2="label-visible" ${attrs} ${lb.visible === false ? '' : 'checked'} title="Show on the plan" aria-label="Show on the plan">
        ${text}${pencil}
        <button class="pl2-iconbtn" type="button" data-pl2="label-remove" data-i="${i}" title="Remove label" aria-label="Remove label">×</button>
      </div>`;
    }
    // A species label is its plant: × removes the plant from the element.
    const sp = state.species[lb.id];
    const keys = lb.keys || [];
    const shown = keys.map((k) => speciesValue(sp, k)).filter(Boolean);
    const values = shown.length
      ? shown.map((v, n) => `<span class="${n ? 'pl2-muted' : ''}">${esc(v)}</span>`).join('')
      : `<span class="pl2-muted">${esc(sp?.name || `Plant #${lb.id}`)} (hidden)</span>`;
    const options = open ? `<div class="pl2-keyoptions" ${attrs}>${speciesKeyOptions(sp, keys, editing.order).map((k) => `
        <label class="pl2-keyoption" data-key="${k}" title="${KEY_NAMES[k]}">
          <span class="pl2-drag" aria-hidden="true">⠿</span>
          <input type="checkbox" data-pl2="label-key" ${attrs} data-key="${k}" ${keys.includes(k) ? 'checked' : ''}>
          <span>${speciesValue(sp, k) ? esc(speciesValue(sp, k)) : `<span class="pl2-muted">${KEY_NAMES[k]} —</span>`}</span>
        </label>`).join('')}</div>` : '';
    return `<div class="pl2-label-row pl2-label-species" data-label-i="${i}">
      ${grip}${plantThumb(sp)}
      <span class="pl2-label-values" data-species-info="${esc(lb.id)}">${values}</span>
      ${pencil}
      <button class="pl2-iconbtn" type="button" data-pl2="species-remove" data-id="${esc(lb.id)}" title="Remove plant" aria-label="Remove plant">×</button>
      ${options}
    </div>`;
  }).join('');
  return rows ? `<div class="pl2-label-list" data-el-id="${esc(el.id)}">${rows}</div>` : '<p class="pl2-muted">No labels or plants.</p>';
}

const KEY_NAMES = { name: 'Common name', scientific_name: 'Scientific name', parent_name: 'Parent name' };

function attributesHtml(state, el, editing) {
  const d = el.data || {};
  return `
    ${subTitle('Labels & plants')}
    ${labelsHtml(state, el, editing)}
    <div class="pl2-btn-row">
      <button class="pl2-btn" type="button" data-pl2="label-add">+ Add label</button>
      <button class="pl2-btn" type="button" data-pl2="species-add">+ Add plant</button>
    </div>
    <div class="pl2-gap"></div>
    ${layerField(state, d.layer)}
    ${datesFields(d.addedDate, d.removedDate)}
    ${notesField(d.notes)}`;
}

function imageAttributesHtml(state, d) {
  return `
    <div class="pl2-field"><label>Name</label><input class="pl2-input" data-pl2="prop-image-name" value="${esc(d.name || '')}"></div>
    ${layerField(state, d.layer)}
    ${datesFields(d.addedDate, d.removedDate)}
    ${notesField(d.notes)}`;
}

// --- Dimensions --------------------------------------------------------------

const readout = (label, value, attrs = '') => `<div class="pl2-field"${attrs}><label>${label}</label><span class="pl2-field__value">${esc(value)}</span></div>`;
const numberField = (label, kind, value, attrs = '') => `<div class="pl2-field"><label>${label}</label><input class="pl2-input" type="number" data-pl2="${kind}" value="${value}"${attrs}></div>`;
const degrees = (rad) => Math.round((rad || 0) * 180 / Math.PI);
// A size in plan units (cm), shown and typed in m or ft.
const lengthField = (label, kind, cm, units) => `<div class="pl2-field"><label>${label}</label><input class="pl2-input" type="number" min="0" step="0.01" data-pl2="${kind}" value="${widthToDisplay(cm || 0, units)}"><span class="pl2-unit">${widthUnit(units)}</span></div>`;

// Measurements first (styled like view mode's Info tab), then the editable
// sizes. Polygons/lines list every segment: straight ones take an exact
// length, curved ones show theirs (resize those by dragging their points).
// Hovering a segment row highlights it on the canvas.
function dimensionsHtml(el, units, pick = null) {
  const d = el.data || {};
  const reads = [];
  if (el.type === 'line') {
    const len = elementLength(el); if (len > 0) reads.push(readout('Length', formatLength(len, units)));
  } else {
    const a = elementArea(el); if (a > 0) reads.push(readout('Area', formatArea(a, units)));
    const p = elementPerimeter(el); if (p > 0) reads.push(readout('Perimeter', formatLength(p, units)));
  }
  const edits = [];
  if (el.type === 'ellipse') edits.push(lengthField('Size', 'prop-width', d.width, units));
  if (el.type === 'rectangle') {
    edits.push(lengthField('Width', 'prop-width', d.width, units), lengthField('Height', 'prop-height', d.height, units));
    edits.push(numberField('Rotation', 'prop-rotation', degrees(d.rotation)));
  }
  if (el.type === 'polygon' || el.type === 'line') edits.push(segmentRows(el, units, pick));
  return `${reads.length ? `<div class="pl2-measure">${reads.join('')}</div>` : ''}${edits.join('')}`;
}

// --- Picked segment ----------------------------------------------------------

// The segment picked on the canvas (or from a side row), if it's this path's.
function pickedSegment(state, el) {
  const pick = state.selectedSegment;
  if (!pick || pick.id !== el.id || (el.type !== 'polygon' && el.type !== 'line')) return null;
  const segs = pathSegments(el.data?.coordinates || [], el.type === 'polygon');
  const at = segs.findIndex((sg) => sg.index === pick.index);
  return at < 0 ? null : { seg: segs[at], number: at + 1, length: segmentLengths(el)[at] };
}

// v1: a picked segment's type is changed in the panel.
function segmentSection(state, el, units, collapsed) {
  const picked = pickedSegment(state, el);
  if (!picked) return '';
  const type = segmentType(picked.seg);
  const opts = SEGMENT_TYPES.map((t) => `<option value="${t}" ${t === type ? 'selected' : ''}>${cap(t)}</option>`).join('');
  return section('segment', `Side ${picked.number}`, `
    <div class="pl2-field"><label>Type</label><select class="pl2-input" data-pl2="prop-segmentType" data-index="${picked.seg.index}">${opts}</select></div>
    ${readout('Length', formatLength(picked.length, units))}
    <p class="pl2-muted">Esc or a click on empty canvas lets go of the side.</p>`, collapsed);
}

function segmentRows(el, units, pick) {
  const sides = straightPathSides(el.data?.coordinates, el.type === 'polygon');
  const sideIndex = new Map(sides.map((side, i) => [side.sideNumber, i]));
  const unit = units === 'imperial' ? 'ft' : 'm';
  const segs = pathSegments(el.data?.coordinates || [], el.type === 'polygon');
  return segmentLengths(el).map((len, i) => {
    // Hovering a row highlights its segment; clicking its label picks it.
    const picked = pick?.id === el.id && pick.index === segs[i]?.index;
    const hover = ` data-segment="${i}" data-id="${esc(el.id)}" data-pl2="segment-pick" data-index="${segs[i]?.index}"${picked ? ' aria-current="true"' : ''}`;
    const straight = sideIndex.get(i + 1);
    if (straight == null) return readout(`Side ${i + 1}`, `${formatLength(len, units)} (curved)`, hover);
    return `<div class="pl2-field"${hover}><label>Side ${i + 1}</label>
      <input class="pl2-input" type="number" min="0" step="0.01" data-pl2="prop-side" data-i="${straight}" value="${displaySideLength(sides[straight].length, units).toFixed(2)}"><span class="pl2-unit">${unit}</span></div>`;
  }).join('');
}

// Width/height with a chain toggle bracketing both (GIMP-style). Unlocking the
// aspect ratio is this deliberate click, never a gesture side effect.
function imageDimensionsHtml(d, natural, units) {
  const width = d.width || natural?.width || 0;
  const height = d.height || natural?.height || 0;
  const locked = d.lockRatio !== false;
  const chainLabel = locked ? 'Aspect ratio locked — click to unlock' : 'Aspect ratio unlocked — click to lock';
  return `
    <div class="pl2-size">
      <div class="pl2-size__fields">
        ${lengthField('Width', 'prop-width', width, units)}
        ${lengthField('Height', 'prop-height', height, units)}
      </div>
      <button class="pl2-chain ${locked ? 'is-locked' : ''}" type="button" data-pl2="prop-lockRatio" aria-pressed="${locked}" title="${chainLabel}" aria-label="${chainLabel}">${icon(locked ? 'link' : 'link-unlink')}</button>
    </div>
    ${numberField('Rotation', 'prop-rotation', degrees(d.rotation))}`;
}

// --- Multi-selection ---------------------------------------------------------

function massPropsHtml(state, sel, collapsed) {
  const els = state.elements.filter((el) => sel.includes(el.id));
  const common = (get) => {
    const vals = new Set(els.map(get));
    return vals.size === 1 ? [...vals][0] : undefined;
  };
  const layer = common((el) => el.data?.layer ?? '');
  const added = common((el) => el.data?.addedDate ?? '');
  const removed = common((el) => el.data?.removedDate ?? '');
  const types = new Set(els.map((el) => el.type));
  const type = types.size === 1 ? [...types][0] : 'mixed';
  const style = {
    fillColor: common((el) => el.data?.fillColor ?? null),
    strokeColor: common((el) => el.data?.strokeColor ?? null),
    strokeWidth: common((el) => el.data?.strokeWidth ?? null),
    canopyStyle: common((el) => el.data?.canopyStyle ?? 'smooth'),
    opacity: common((el) => el.data?.opacity ?? 200),
    visible: common((el) => el.data?.visible !== false),
  };
  const attributes = `
    <p class="pl2-muted">Edits apply to the whole selection.</p>
    ${layerField(state, layer, layer === undefined)}
    ${datesFields(added, removed, { mixedAdded: added === undefined, mixedRemoved: removed === undefined })}`;
  return `
    <h3 class="pl2-section-title">${sel.length} elements</h3>
    ${section('attributes', 'Attributes', attributes, collapsed)}
    ${section('appearance', 'Appearance', appearanceHtml(style, { type, units: state.settings.units }), collapsed)}`;
}

// --- Draw mode ---------------------------------------------------------------

const DRAW_NOUN = { rectangle: 'rectangle', ellipse: 'circle', polygon: 'polygon', line: 'line', placement: 'plant' };

// Polygons and lines: what each new segment is drawn as.
function drawSegmentField(tool, style = {}) {
  if (tool !== 'polygon' && tool !== 'line') return '';
  const current = style.segmentType || 'straight';
  const opts = SEGMENT_TYPES.map((t) => `<option value="${t}" ${t === current ? 'selected' : ''}>${cap(t)}</option>`).join('');
  return `<div class="pl2-field"><label>Segments</label><select class="pl2-input" data-pl2="prop-segmentType">${opts}</select></div>`;
}

function drawPropsHtml(state, collapsed) {
  const tool = state.activeTool;
  const styles = state.drawStyles || defaultDrawStyles();
  const shared = styles.shared || {};
  const placing = tool === 'placement';
  const title = placing ? `Place ${esc(state.pendingSpecies?.name || 'plant')}` : `New ${DRAW_NOUN[tool]}`;
  return `
    <div data-props-target="draw" data-tool="${tool}">
      <h3 class="pl2-section-title">${title}</h3>
      <p class="pl2-muted">What the next ${DRAW_NOUN[tool]} you ${placing ? 'place' : 'draw'} looks like.</p>
      ${section('draw-appearance', 'Appearance', drawSegmentField(tool, styles[tool]) + appearanceHtml(styles[tool] || {}, { type: placing ? 'ellipse' : tool, units: state.settings.units }), collapsed)}
      ${section('draw-attributes', 'Attributes', layerField(state, shared.layer) + datesFields(shared.addedDate, shared.removedDate), collapsed)}
    </div>`;
}
